import { scheduleSaveButton } from 'owa-locstrings/lib/strings/schedulesavebutton.locstring.json';
import { composeDiscardButton } from 'owa-locstrings/lib/strings/composediscardbutton.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
    Dialog,
    DialogType,
    DialogFooter,
    IDialogContentStyles,
    IDialogStyles,
    IDialogFooterStyles,
} from 'office-ui-fabric-react/lib/Dialog';
import { observer } from 'mobx-react';
import {
    setShouldShowPivotEnableDisableDialog,
    pivotToggled,
    focusedOtherPivotsToggled,
} from '../actions/internalActions';
import { showPivotEnableDisableDialog } from '../actions/publicActions';
import { orchestrator } from 'satcheljs';
import { Toggle, IToggleStyles } from 'office-ui-fabric-react/lib/Toggle';
import store from '../store/Store';
import isFocusedOtherPivotsEnabled from '../selectors/isFocusedOtherPivotsEnabled';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { getPivotPreviewSvg } from '../selectors/getPivotPreviewSvg';

import { resetFocus } from 'owa-mail-focus-manager';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import InboxPlusPivotViewState from 'owa-mail-inbox-plus-store/lib/store/schema/InboxPlusPivotViewState';
import { onPivotEnableDisableDialogSave } from 'owa-mail-inbox-plus-store';
import getInboxPlusPivotDisplayProperties from 'owa-mail-inbox-plus-store/lib/selectors/getInboxPlusPivotDisplayProperties';

const LISTVIEW_PREVIEW_SVG = require('../svg/dialog_illustration_bckgrd.svg');

const styles = require('./PivotEnableDisableDialog.scss');

interface PivotEnableDisableDialogProps {
    onClose: () => void;
}
@observer
class PivotEnableDisableDialog extends React.Component<PivotEnableDisableDialogProps> {
    render() {
        return (
            <Dialog
                onDismiss={this.onDismiss}
                hidden={!store.isPivotEnableDisableDialogShown}
                dialogContentProps={{
                    type: DialogType.normal,
                    //TODO: VSO 40597 -- Inbox Plus Loc strings
                    title: 'Inbox tabs',
                    subText:
                        'Organize your messages by separating them into tabs. To go back to your old Inbox, turn off all tabs​.',
                    styles: this.getDialogContentStyles,
                }}
                styles={this.getDialogStyles}>
                {this.renderDialogBody()}
                {this.renderDialogFooter()}
            </Dialog>
        );
    }

    private onDismiss = () => {
        setShouldShowPivotEnableDisableDialog(false);
        this.props.onClose();
    };

    private onSave = () => {
        setShouldShowPivotEnableDisableDialog(false);
        onPivotEnableDisableDialogSave(store.enabledDisabledPivots);
        this.props.onClose();
    };

    private renderDialogBody = () => {
        const toggles = [];
        const pivotTogglesToRender = store.enabledDisabledPivots.filter(
            (pivot: InboxPlusPivotViewState) =>
                pivot.InboxClassificationFilter !== InboxClassificationFilter.None &&
                pivot.InboxClassificationFilter !== InboxClassificationFilter.Focused &&
                pivot.InboxClassificationFilter !== InboxClassificationFilter.Other
        );

        toggles.push(
            <Toggle
                checked={isFocusedOtherPivotsEnabled()}
                //TODO: VSO 40597 -- Inbox Plus Loc strings
                label="Focused/Other"
                inlineLabel={true}
                styles={this.getToggleStyles}
                onChange={this.focusedOtherPivotsToggled}
            />
        );

        for (const pivot of pivotTogglesToRender) {
            const pivotDisplayProperties = getInboxPlusPivotDisplayProperties(
                pivot.InboxClassificationFilter
            );
            const toggleFunction = this.getToggleFunction(pivot);
            toggles.push(
                <Toggle
                    checked={pivot.isEnabled}
                    label={pivotDisplayProperties.displayText}
                    inlineLabel={true}
                    styles={this.getToggleStyles}
                    onChange={toggleFunction}
                />
            );
        }

        return (
            <div className={styles.bodyContainer}>
                <div>{toggles}</div>
                <div className={styles.previewContainer}>
                    <img src={LISTVIEW_PREVIEW_SVG} />
                    <div className={styles.pivotPreviews}>
                        {this.getPivotPreviewsToRender(pivotTogglesToRender)}
                    </div>
                </div>
            </div>
        );
    };

    private renderDialogFooter = (): JSX.Element => {
        return (
            <DialogFooter styles={this.getFooterStyles}>
                <PrimaryButton text={loc(scheduleSaveButton)} onClick={this.onSave} />
                <DefaultButton text={loc(composeDiscardButton)} onClick={this.onDismiss} />;
            </DialogFooter>
        );
    };

    private getPivotPreviewsToRender = (
        pivotsToRender: InboxPlusPivotViewState[]
    ): JSX.Element[] => {
        const pivotPreviews = [];
        const previewsToRender = pivotsToRender
            .filter(pivot => pivot.isEnabled)
            .map(pivot => pivot.InboxClassificationFilter);

        // If user has F/O enabled, add the F/O pivots to the beginning/end of the array, else add Inbox to the beginning
        if (isFocusedOtherPivotsEnabled()) {
            previewsToRender.unshift(InboxClassificationFilter.Focused);
            previewsToRender.push(InboxClassificationFilter.Other);
        } else {
            previewsToRender.unshift(InboxClassificationFilter.None);
        }

        for (const inboxClassificationFilter of previewsToRender) {
            pivotPreviews.push(
                <img
                    className={
                        inboxClassificationFilter === InboxClassificationFilter.None ||
                        inboxClassificationFilter === InboxClassificationFilter.Focused
                            ? styles.previewIconSelected
                            : styles.previewIconUnselected
                    }
                    src={getPivotPreviewSvg(inboxClassificationFilter)}
                />
            );
        }

        return pivotPreviews;
    };

    private focusedOtherPivotsToggled = (evt: React.MouseEvent, checked: boolean) => {
        focusedOtherPivotsToggled(checked);
    };

    private getToggleFunction = (pivot: InboxPlusPivotViewState) => {
        return (evt, checked) => pivotToggled(pivot, checked);
    };

    private getFooterStyles = (): Partial<IDialogFooterStyles> => {
        return {
            actions: styles.footerActions,
            actionsRight: styles.footerActionsRight,
        };
    };

    private getDialogStyles = (): Partial<IDialogStyles> => {
        return {
            main: styles.dialogMainStyles,
        };
    };

    private getDialogContentStyles = (): Partial<IDialogContentStyles> => {
        return {
            content: styles.dialogContent,
            title: styles.dialogTitle,
            inner: styles.dialogInner,
            subText: styles.dialogSubtext,
        };
    };

    private getToggleStyles = (): Partial<IToggleStyles> => {
        return {
            root: styles.toggleRoot,
            label: styles.toggleLabel,
        };
    };
}

orchestrator(showPivotEnableDisableDialog, actionMessage => {
    setShouldShowPivotEnableDisableDialog(true);
    const pivotEnableDisableDialogDiv = document.createElement('div');
    pivotEnableDisableDialogDiv.id = 'pivotEnableDisableDialog';
    document.body.appendChild(pivotEnableDisableDialogDiv);
    const dialogCloseProp = () => {
        ReactDOM.unmountComponentAtNode(pivotEnableDisableDialogDiv);
        document.body.removeChild(pivotEnableDisableDialogDiv);
        resetFocus();
    };

    ReactDOM.render(
        <PivotEnableDisableDialog onClose={dialogCloseProp} />,
        pivotEnableDisableDialogDiv
    );
});
